import api from './api';
import { Organization, OrganizationType } from '../types';

export interface PlatformStats {
  totalOrganizations: number;
  activeOrganizations: number;
  inactiveOrganizations: number;
  totalUsers: number;
  totalTransfers: number;
  planDistribution: { plan: string; count: number }[];
  recentOrganizations: {
    id: string;
    name: string;
    contactEmail: string;
    subscriptionPlan: string;
    isActive: boolean;
    createdAt: string;
  }[];
  monthlyGrowth: { month: string; count: number }[];
}

export const organizationsService = {
  getPlatformStats: async (): Promise<PlatformStats> => {
    const response = await api.get('/organizations/stats/platform');
    return response.data;
  },

  getAll: async (filters?: {
    type?: OrganizationType;
    isActive?: boolean;
  }): Promise<Organization[]> => {
    const params = new URLSearchParams();
    if (filters?.type) params.append('type', filters.type);
    if (filters?.isActive !== undefined)
      params.append('isActive', String(filters.isActive));

    const response = await api.get(
      `/organizations${params.toString() ? `?${params.toString()}` : ''}`,
    );
    return response.data;
  },

  getById: async (id: string): Promise<Organization> => {
    const response = await api.get(`/organizations/${id}`);
    return response.data;
  },

  getChildren: async (parentId: string): Promise<Organization[]> => {
    const response = await api.get(`/organizations/${parentId}/children`);
    return response.data;
  },

  create: async (data: {
    name: string;
    contactEmail: string;
    contactPhone?: string;
    address?: string;
    type?: OrganizationType;
    parentOrganizationId?: string;
    subscriptionPlan?: string;
  }): Promise<Organization> => {
    const response = await api.post('/organizations', data);
    return response.data;
  },

  update: async (
    id: string,
    data: Partial<{
      name: string;
      contactEmail: string;
      contactPhone?: string;
      address?: string;
      type?: OrganizationType;
      parentOrganizationId?: string;
      subscriptionPlan?: string;
      isActive?: boolean;
      smsEnabled?: boolean;
    }>,
  ): Promise<Organization> => {
    const response = await api.patch(`/organizations/${id}`, data);
    return response.data;
  },

  getDependencies: async (id: string): Promise<{
    organizationId: string;
    organizationName: string;
    isActive: boolean;
    autoRemovable: Record<string, number>;
    blocking: Record<string, number>;
    totalBlocking: number;
    totalAutoRemovable: number;
    canDelete: boolean;
  }> => {
    const response = await api.get(`/organizations/${id}/dependencies`);
    return response.data;
  },

  deactivate: async (id: string): Promise<Organization> => {
    const response = await api.patch(`/organizations/${id}/deactivate`);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/organizations/${id}`);
  },

  getDeleted: async (): Promise<Organization[]> => {
    const response = await api.get('/organizations/deleted/list');
    return response.data;
  },

  restore: async (id: string): Promise<Organization> => {
    const response = await api.patch(`/organizations/${id}/restore`);
    return response.data;
  },

  getOrgStats: async (id: string): Promise<{
    organizationId: string;
    userCount: number;
    transferCount: number;
    activeTransferCount: number;
  }> => {
    const response = await api.get(`/organizations/${id}/stats`);
    return response.data;
  },

  sendDemoEmail: async (data: { email: string; name: string; companyName?: string }): Promise<{ success: boolean }> => {
    const response = await api.post('/organizations/send-demo-email', data);
    return response.data;
  },
};
